/**
 * Shared list-region states — skeleton, inline error, empty and
 * filter-empty — used by every screen that renders a data list or grid.
 * Built only from shadcn/ui primitives; no bespoke CSS.
 */
import { CircleAlert, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

export function SkeletonRows({ rows = 6 }: { rows?: number }) {
  const keys = Array.from({ length: rows }, (_, i) => `row-${i}`)
  return (
    <div className="flex flex-col gap-1" aria-hidden="true">
      {keys.map((key) => (
        <div key={key} className="flex items-center gap-3 rounded-lg px-2 py-3">
          <Skeleton className="size-8 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function SkeletonCards({ count = 4 }: { count?: number }) {
  const keys = Array.from({ length: count }, (_, i) => `card-${i}`)
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4" aria-hidden="true">
      {keys.map((key) => (
        <div key={key} className="flex flex-col gap-3 rounded-lg border bg-card p-5 shadow-resting">
          <Skeleton className="h-3 w-2/5" />
          <Skeleton className="h-7 w-1/3" />
          <Skeleton className="h-3 w-3/5" />
        </div>
      ))}
    </div>
  )
}

/**
 * Something failed to load: said at once to a screen reader, marked apart from
 * an empty list (an empty list has nothing wrong with it), and the way out.
 */
export function ErrorState({ title, onRetry }: { title: string; onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-8 text-center"
    >
      <CircleAlert className="size-5 text-destructive" strokeWidth={1.8} aria-hidden="true" />
      <p className="text-sm text-foreground">{title}</p>
      <Button variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </div>
  )
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon
  title: string
  /** Only when the title and button don't say it (docs/UX-GUIDE.md → Words). */
  description?: string
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center">
      <div className="flex size-10 items-center justify-center rounded-full bg-muted">
        <Icon className="size-5 text-muted-foreground" strokeWidth={1.8} aria-hidden="true" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-base font-semibold text-foreground">{title}</p>
        {description ? (
          <p className="max-w-measure text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action ? <Button onClick={action.onClick}>{action.label}</Button> : null}
    </div>
  )
}

export function FilterEmptyState({
  description,
  onClear,
  clearLabel = 'Clear search',
}: {
  description: string
  onClear: () => void
  /** What the button says: "Clear search" unless a filter, not a search, is what's on. */
  clearLabel?: string
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center">
      <p className="max-w-measure text-sm text-muted-foreground">{description}</p>
      <Button variant="outline" onClick={onClear}>
        {clearLabel}
      </Button>
    </div>
  )
}
